import React from 'react';
import { Home, CheckCircle, TrendingUp, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import SellerLeadForm from '../components/forms/SellerLeadForm';

const SellPage = () => {
  const benefits = [
    {
      icon: TrendingUp,
      title: 'Market Analysis',
      description: 'Get a comprehensive market analysis to price your home competitively and maximize your return.',
    },
    {
      icon: Users,
      title: 'Expert Marketing',
      description: 'Professional photography, virtual tours, and targeted marketing to reach qualified buyers.',
    },
    {
      icon: Home,
      title: 'Full Service',
      description: 'From staging advice to closing, I handle every detail of your home sale.',
    },
  ];

  const process = [
    { step: 1, title: 'Free Consultation', description: 'I meet with you to discuss your goals and timeline' },
    { step: 2, title: 'Home Evaluation', description: 'Professional assessment of your property\'s value' },
    { step: 3, title: 'Prepare & List', description: 'Strategic pricing and marketing launch' },
    { step: 4, title: 'Showings & Offers', description: 'I manage all showings and negotiate offers on your behalf' },
    { step: 5, title: 'Close the Deal', description: 'Seamless transaction to closing day' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div 
        className="relative py-24 bg-cover bg-center"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1920)' }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative max-w-7xl mx-auto px-4 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Sell Your Home With Confidence</h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Get top dollar for your home with Alexander Conti's proven selling strategy
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Benefits */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 -mt-12 mb-16 relative z-10">
          {benefits.map((benefit, idx) => (
            <Card key={idx} className="shadow-xl border-0 bg-white">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="text-red-600" size={28} />
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left - Process */}
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-8">My Selling Process</h2>
            <div className="space-y-6">
              {process.map((item) => (
                <div key={item.step} className="flex gap-4">
                  <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Why Choose Us */}
            <div className="mt-12 p-6 bg-gray-900 rounded-xl text-white">
              <h3 className="font-bold text-xl mb-4">Why Sell With Alexander Conti?</h3>
              <ul className="space-y-3">
                {[
                  'Local market expert with proven results',
                  'Average sale price 5% above market value',
                  'Homes sell 20% faster than average',
                  'Professional staging and photography included',
                  'Transparent communication throughout',
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <CheckCircle size={18} className="text-red-500 flex-shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right - Form */}
          <div>
            <SellerLeadForm 
              title="Get Your Free Home Evaluation"
              subtitle="Find out what your home is worth in today's market"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellPage;
